import React from "react";
import Icon from "./Icon";
import "./ui.css";

export const BUTTON_VARIANTS = [
    "primary",
    "secondary",
    "danger",
    "danger-ghost",
    "success",
    "ghost",
    "soft",
    "accent",
    "glass",
];

export const BUTTON_SIZES = ["sm", "md", "lg"];

/**
 * Старые значения пропа `type` из BlockTitle/PatientPlan.
 * Нужны, чтобы существующие экраны работали без правок.
 */
const LEGACY_TYPES = {
    normal: "primary",
    primary: "primary",
    light: "secondary",
    secondary: "secondary",
    danger: "danger",
    "danger-light": "danger-ghost",
    success: "success",
    ghost: "ghost",
    soft: "soft",
    accent: "accent",
    glass: "glass",
    // Bootstrap-совместимые имена (перенос футеров модалок и пагинации)
    "outline-primary": "secondary",
    "outline-secondary": "secondary",
    "outline-dark": "secondary",
    "outline-danger": "danger-ghost",
    link: "ghost",
};

const cx = (...parts) => parts.filter(Boolean).join(" ");

/**
 * Разбирает легаси-строку вида "light icon" на вариант и признак icon-only.
 */
export const normalizeLegacyType = (type) => {
    const tokens = String(type || "")
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);
    const variant = LEGACY_TYPES[tokens[0]] || "primary";
    return { variant, legacyIcon: tokens.includes("icon") };
};

/**
 * Единая кнопка приложения.
 *
 * <Button>Сохранить</Button>
 * <Button variant="secondary" icon="edit">Редактировать</Button>
 * <Button variant="danger" icon="trash" iconOnly title="Удалить" />
 * <Button variant="primary" loading>Сохранить</Button>
 *
 * Пропсы:
 *  variant      primary | secondary | danger | danger-ghost | success | ghost
 *               | soft (светло-синяя заливка) | accent (жёлтая) | glass (на тёмном фоне)
 *  size         sm | md | lg (по умолчанию md)
 *  icon         имя из Icon registry (print, trash, back, edit, add, save…)
 *               либо готовый React-элемент
 *  iconPosition left (по умолчанию) | right
 *  iconOnly     только иконка: круг + обязательно title/aria-label
 *  loading      показывает спиннер и блокирует нажатие
 *  block        растянуть на всю ширину
 *  type         сохраняется для совместимости: "light icon" и т.п.
 */
const Button = React.forwardRef(function Button(
    {
        as: Component = "button",
        variant,
        size = "md",
        icon,
        iconPosition = "left",
        iconOnly = false,
        loading = false,
        block = false,
        type = "button",
        className,
        children,
        title,
        "aria-label": ariaLabel,
        disabled,
        ...rest
    },
    ref,
) {
    const legacy = normalizeLegacyType(variant);
    const resolvedVariant = legacy.variant;
    // «icon» в legacy-строке означает «кнопка с иконкой», а не «только иконка».
    // Только иконка — это явный проп iconOnly.
    const resolvedIconOnly = Boolean(iconOnly);
    const isNativeButton = Component === "button";

    let iconNode = null;
    if (icon) {
        iconNode =
            React.isValidElement(icon) || typeof icon !== "string" ? (
                icon
            ) : (
                <Icon name={icon} />
            );
    }

    const text =
        children === undefined || children === null
            ? ""
            : typeof children === "string" || typeof children === "number"
              ? String(children)
              : children;

    const hasText = text !== "" && resolvedIconOnly === false;
    const label = ariaLabel || title || (typeof text === "string" ? text : "");

    const classes = cx(
        "ui-btn",
        `ui-btn--${resolvedVariant}`,
        `ui-btn--${size}`,
        resolvedIconOnly && "ui-btn--icon",
        block && "ui-btn--block",
        iconNode && "ui-btn--with-icon",
        iconNode && iconPosition === "right" && "ui-btn--reverse",
        loading && "is-loading",
        disabled && "is-disabled",
        className,
    );

    // Для ссылки (<Button as="a">) недоступные атрибуты кнопки не передаём.
    const semanticProps = isNativeButton
        ? { disabled: disabled || loading, type }
        : {
              "aria-disabled": disabled || loading || undefined,
              role: undefined,
              tabIndex: disabled ? -1 : undefined,
          };

    return (
        <Component
            ref={ref}
            aria-busy={loading || undefined}
            aria-label={resolvedIconOnly ? label : ariaLabel}
            className={classes}
            title={title || (resolvedIconOnly ? label : undefined)}
            {...semanticProps}
            {...rest}>
            {loading ? (
                <span className="ui-btn__spinner" aria-hidden="true" />
            ) : (
                iconNode && (
                    <span className="ui-btn__icon" aria-hidden="true">
                        {iconNode}
                    </span>
                )
            )}
            {hasText ? <span className="ui-btn__text">{text}</span> : null}
        </Component>
    );
});

export default Button;

